import React from "react";
import styled from "styled-components";
import { Application, DynamicConditionTerm, PaymentModel, makeRequest } from "../../common/api";
import { checkConditionTerm } from "../../common/features/DynamicConditions";

declare var Plotly;

const Container = styled.div`
  padding: 20px;
`;

const InfoText = styled.p`
  margin-bottom: 24px;
  color: #666;
`;

const ControlsContainer = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const ChartContainer = styled.div`
  margin-bottom: 40px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
  align-items: flex-start;
  
  .label-text {
    white-space: nowrap;
    margin-bottom: 4px;
    font-weight: 500;
    align-self: flex-start;
  }
  
  select {
    padding: 6px;
    min-width: 200px;
    width: 100%;
    border-radius: 4px;
    border: 1px solid #ccc;
  }
`;

interface Props {
  applications: Application[];
  mappings: { [key: string]: string };
}
interface State {
  selectedGroupByName: string;
  secondaryGroupBy: string;
  viewType: "day" | "week";
  selectedBatchId: string;
  paymentPlansBatchId: string;
  paymentPlansStatus: string;
  paymentModels: PaymentModel[] | null;
  portalEnrolleesCountBatchId: string;
}

const PAYMENT_PLAN_STATUSES = [
  { value: "ALL", label: "All" },
  { value: "CONTRACT_SIGNED", label: "Contract Signed" },
  { value: "COMPLETE", label: "Complete" }
];

export class DashboardTrendsPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // Get first batch ID from applications if available
    const firstBatchId = props.applications.length > 0 ? props.applications[0].batchId : "";

    this.state = {
      selectedGroupByName: CONDITIONS[0].name,
      secondaryGroupBy: "",
      viewType: "day",
      selectedBatchId: firstBatchId,
      paymentPlansBatchId: firstBatchId,
      paymentPlansStatus: "ALL",
      paymentModels: null,
      portalEnrolleesCountBatchId: firstBatchId
    };
  }

  componentDidMount() {
    const existingScript = document.getElementById("plotly-script");
    if (!existingScript) {
      const scriptTag = document.createElement("script");
      scriptTag.id = "plotly-script";
      scriptTag.src = "/lib/plotly.js";
      scriptTag.type = "text/javascript";
      scriptTag.onload = () => {
        this.renderPlot();
        this.renderEnrollmentStatusPieChart();
        this.renderPaymentPlansChart();
        this.loadPaymentModels();
      }
      document.body.appendChild(scriptTag);
    } else {
      this.renderPlot();
      this.renderEnrollmentStatusPieChart();
      this.renderPaymentPlansChart();
      this.loadPaymentModels();
    }
  }

  componentDidUpdate() {
    setTimeout(() => {
      this.renderPlot();
      this.renderEnrollmentStatusPieChart();
      this.renderPaymentPlansChart();
    }, 30);
  }

  // Get unique batch IDs and their names from applications
  getBatches() {
    const batchMap = new Map();
    this.props.applications.forEach(app => {
      if (!batchMap.has(app.batchId)) {
        const batchName = this.props.mappings[app.batchId] || app.batchId;
        batchMap.set(app.batchId, batchName);
      }
    });
    return Array.from(batchMap.entries()).map(([id, name]) => ({ id, name }));
  }

  loadPaymentModels = async () => {
    const paymentModels = await makeRequest("/api/paymentModels", "GET");
    this.setState({ paymentModels }, () => {
      this.renderPaymentPlansChart();
    });
  }

  renderPieChart(id: string, values: any, title: string) {
    const data = [{
      values: Object.values(values),
      labels: Object.keys(values),
      type: 'pie',
      hole: 0.4,
      textinfo: "label+value+percent",
      insidetextorientation: "radial"
    }];

    const layout = {
      title: title,
      height: 400,
      showlegend: true
    };
    Plotly.react(id, data, layout);

  }

  renderEnrollmentStatusPieChart() {
    const { selectedBatchId } = this.state;
    
    // Filter applications for selected batch
    const batchApplications = this.props.applications.filter(
      app => app.batchId === selectedBatchId
    );

    // Count applications by status
    const statusCounts = {};
    batchApplications.forEach(app => {
      statusCounts[app.status] = (statusCounts[app.status] || 0) + 1;
    });

    this.renderPieChart("plotlyEnrollmentStatusChart", statusCounts, `Enrollment Status Distribution for ${this.props.mappings[selectedBatchId] || selectedBatchId} (${batchApplications.length} total)`);
  }

  renderPaymentPlansChart() {
    const { paymentPlansBatchId, paymentPlansStatus, paymentModels } = this.state;
    
    if (!paymentModels) return; // Don't render if we don't have payment models yet
    
    // Filter applications for selected batch and status
    const filteredApplications = this.props.applications.filter(app => {
      const batchMatches = app.batchId === paymentPlansBatchId;
      if (!batchMatches) return false;
      
      if (paymentPlansStatus === "ALL") return true;
      
      return app.status === paymentPlansStatus;
    });

    const paymentPlanCounts = {};
    filteredApplications.forEach(app => {
      const modelId = app.selectedPaymentModelId;
      const model = modelId ? paymentModels.find(m => m._id === modelId) : null;
      const planName = model ? model.name : 'No Plan';
      paymentPlanCounts[planName] = (paymentPlanCounts[planName] || 0) + 1;
    });

    this.renderPieChart("plotlyPaymentPlansChart", paymentPlanCounts, `Payment Plan Distribution for ${this.props.mappings[paymentPlansBatchId] || paymentPlansBatchId} (${paymentPlansStatus})}`);
  }

  renderPlot() {
    const term = CONDITIONS.find(
      (condition) => condition.name === this.state.selectedGroupByName
    );
    const secondaryTerm = this.state.secondaryGroupBy
      ? CONDITIONS.find(
          (condition) => condition.name === this.state.secondaryGroupBy
        )
      : null;
    
    // Filter applications by selected batch
    const filteredApplications = this.props.applications.filter(
      app => this.state.portalEnrolleesCountBatchId === "ALL" || app.batchId === this.state.portalEnrolleesCountBatchId
    );

    const data = groupByDate(
      filteredApplications,
      term,
      secondaryTerm,
      this.props.mappings,
      this.state.viewType
    );
    Plotly.react("plotlyRoot", data, {
      barmode: "stack",
      title: `Enrollments by ${this.state.selectedGroupByName}${this.state.secondaryGroupBy ? ` and ${this.state.secondaryGroupBy}` : ''}`,
      height: 400,
      xaxis: {
        type: 'category',
        categoryorder: 'category ascending'
      },
      yaxis: {
        tickformat: 'd',  
        dtick: 1 
      }
    });
  }

  render() {
    const batches = this.getBatches();
    
    return (
      <Container>
        <InfoText>
          'Trends' is a work in progress. And only available for Ongoing Batch/Course Enrollments.
        </InfoText>
        <h3>Portal Enrollees Count</h3>
        <ControlsContainer>
          <Label style={{ minWidth: "300px" }}>
            <span className="label-text">Select Batch:</span>
            <select
              value={this.state.portalEnrolleesCountBatchId}
              onChange={(e) => this.setState({ portalEnrolleesCountBatchId: e.target.value })}
            >
              <option value="ALL">All Batches</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
          </Label>
          <Label>
            <span className="label-text">Group by:</span>
            <select
              value={this.state.selectedGroupByName}
              onChange={(e) =>
                this.setState({
                  selectedGroupByName: e.target.value,
                  secondaryGroupBy: "",
                })
              }
            >
              {CONDITIONS.map((condition) => (
                <option key={condition.name} value={condition.name}>
                  {condition.name}
                </option>
              ))}
            </select>
          </Label>
          <Label>
            <span className="label-text">Secondary group by:</span>
            <select
              value={this.state.secondaryGroupBy}
              onChange={(e) =>
                this.setState({ secondaryGroupBy: e.target.value })
              }
            >
              <option key={""} value={""}>
                None
              </option>
              {CONDITIONS.filter(
                (c) => c.name !== this.state.selectedGroupByName
              ).map((condition) => (
                <option key={condition.name} value={condition.name}>
                  {condition.name}
                </option>
              ))}
            </select>
          </Label>
          <Label>
            <span className="label-text">View:</span>
            <select
              value={this.state.viewType}
              onChange={(e) => this.setState({ viewType: e.target.value as "day" | "week" })}
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
            </select>
          </Label>
        </ControlsContainer>
        <ChartContainer>
          <div id="plotlyRoot" style={{ minHeight: "400px" }}></div>
        </ChartContainer>

        <h3>Enrollment Status Distribution</h3>
        <ControlsContainer>
          <Label style={{ minWidth: "300px" }}>
            <span className="label-text">Select Batch:</span>
            <select
              value={this.state.selectedBatchId}
              onChange={(e) => this.setState({ selectedBatchId: e.target.value })}
            >
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
          </Label>
        </ControlsContainer>

        <ChartContainer>
          <div id="plotlyEnrollmentStatusChart" style={{ minHeight: "400px" }}></div>
        </ChartContainer>

        <h3>Payment Plans Distribution</h3>
        <ControlsContainer>
          <Label style={{ minWidth: "300px" }}>
            <span className="label-text">Select Batch:</span>
            <select
              value={this.state.paymentPlansBatchId}
              onChange={(e) => this.setState({ paymentPlansBatchId: e.target.value })}
            >
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
          </Label>

          <Label style={{ minWidth: "200px" }}>
            <span className="label-text">Select Status:</span>
            <select
              value={this.state.paymentPlansStatus}
              onChange={(e) => this.setState({ paymentPlansStatus: e.target.value })}
            >
              {PAYMENT_PLAN_STATUSES.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </Label>
        </ControlsContainer>

        <ChartContainer>
          <div id="plotlyPaymentPlansChart" style={{ minHeight: "400px" }}></div>
        </ChartContainer>
      </Container>
    );
  }
}

// Either a condition, or a field that is group-by-ed
interface DynamicPivot extends DynamicConditionTerm {
  name: string;
  excludedName?: string; // The name of an item that doesn't meet the condition
}

const CONDITIONS: DynamicPivot[] = [
  {
    name: "Batch",
    target: "FIELD",
    targetDetails: "batchId",
    operator: "Group by",
  },
  {
    name: "Tracking",
    target: "TRACKING",
    operator: "Group by",
  },
];

// Helper function to format date for display and sorting
const formatDateString = (date: Date, viewType: "day" | "week"): string => {
  if (viewType === "day") {
    date.setHours(0, 0, 0, 0);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } else {
    // Set to start of week (Sunday)
    const dayOfWeek = date.getDay();
    date.setDate(date.getDate() - dayOfWeek);
    date.setHours(0, 0, 0, 0);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `Week of ${month}/${day}/${year}`;
  }
};

const groupByDate = (
  applications: Application[],
  term: DynamicPivot,
  secondaryTerm: DynamicPivot | null,
  mappings: { [key: string]: string },
  viewType: "day" | "week"
) => {
  const dataMap = {};

  // Collect all unique dates
  const allDates = new Set<string>();
  for (let application of applications) {
    const date = new Date(application.createdAt);
    const dateString = formatDateString(date, viewType);
    allDates.add(dateString);
  }

  // Sort dates (now they sort correctly as strings due to zero-padding)
  const sortedDates = Array.from(allDates).sort();

  // Process applications and build data
  for (let application of applications) {
    const date = new Date(application.createdAt);
    const dateString = formatDateString(date, viewType);

    const isAMatch = checkConditionTerm(term, application, []);
    const dataKey = computeDataKey(term, isAMatch, application, mappings);
    if (secondaryTerm) {
      const isASecondaryMatch = checkConditionTerm(secondaryTerm, application, []);
      const secondaryDataKey = computeDataKey(secondaryTerm, isASecondaryMatch, application, mappings);
      addOrIncrementDataSorted(dataMap, dataKey + " > " + secondaryDataKey, dateString, sortedDates);
    } else {
      addOrIncrementDataSorted(dataMap, dataKey, dateString, sortedDates);
    }
  }

  return Object.values(dataMap);
};

const addOrIncrementDataSorted = (
  dataMap: any,
  dataKey: string,
  dateString: string,
  sortedDates: string[]
) => {
  if (!dataMap[dataKey]) {
    // Initialize with all sorted dates and zero values
    dataMap[dataKey] = {
      x: [...sortedDates],
      y: new Array(sortedDates.length).fill(0),
      name: dataKey,
      type: "bar",
    };
  }
  
  const data = dataMap[dataKey];
  const dateIndex = data.x.indexOf(dateString);
  if (dateIndex !== -1) {
    data.y[dateIndex]++;
  }
};

const computeDataKey = (
  term: DynamicPivot,
  isAMatch: boolean,
  entry: any,
  mappings: { [key: string]: string }
) => {
  if (term.operator === "Greater than or equal" || term.operator === "Exists") {
    return isAMatch ? term.name : term.excludedName || "Not " + term.name;
  } else if (term.target === "FIELD" && term.targetDetails) {
    const value = entry[term.targetDetails];
    return mappings[value] || value;
  } else if (term.target === "TRACKING") {
    const tracking = entry.tracking;
    if (tracking) {
      return entry.tracking.source + " / " + entry.tracking.medium + " / " + entry.tracking.campaign + " / " + entry.tracking.content;
    } else {
      return "No tracking";
    }
  } else {
    // Unexpected case
    return term.name;
  }
};

